import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X, LucideIcon } from 'lucide-react';

/**
 * Toast notification system with modern gradients and glassmorphism
 */

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
  duration: number;
}

interface ToastContextValue {
  addToast: (message: string, type?: ToastType, duration?: number) => number;
  removeToast: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: number): void => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback((message: string, type: ToastType = 'info', duration: number = 5000): number => {
    const id = Date.now();
    const toast: Toast = { id, message, type, duration };

    setToasts((prev) => [...prev, toast]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, [removeToast]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}

interface ToastContainerProps {
  toasts: Toast[];
  removeToast: (id: number) => void;
}

function ToastContainer({ toasts, removeToast }: ToastContainerProps): JSX.Element {
  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
      ))}
    </div>
  );
}

interface ToastItemProps {
  toast: Toast;
  onClose: () => void;
}

interface TypeConfig {
  bg: string;
  border: string;
  text: string;
  icon: LucideIcon;
  iconColor: string;
  closeHover: string;
}

const typeConfig: Record<ToastType, TypeConfig> = {
  success: {
    bg: 'bg-gradient-to-r from-success-50 to-success-100',
    border: 'border-l-4 border-success-500',
    text: 'text-success-800',
    icon: CheckCircle,
    iconColor: 'text-success-500',
    closeHover: 'hover:bg-success-200 hover:text-success-800'
  },
  error: {
    bg: 'bg-gradient-to-r from-danger-50 to-danger-100',
    border: 'border-l-4 border-danger-500',
    text: 'text-danger-800',
    icon: XCircle,
    iconColor: 'text-danger-500',
    closeHover: 'hover:bg-danger-200 hover:text-danger-800'
  },
  warning: {
    bg: 'bg-gradient-to-r from-warning-50 to-warning-100',
    border: 'border-l-4 border-warning-500',
    text: 'text-warning-800',
    icon: AlertTriangle,
    iconColor: 'text-warning-500',
    closeHover: 'hover:bg-warning-200 hover:text-warning-800'
  },
  info: {
    bg: 'bg-gradient-to-r from-primary-50 to-primary-100',
    border: 'border-l-4 border-primary-500',
    text: 'text-primary-800',
    icon: Info,
    iconColor: 'text-primary-500',
    closeHover: 'hover:bg-primary-200 hover:text-primary-800'
  }
};

function ToastItem({ toast, onClose }: ToastItemProps): JSX.Element {
  const { message, type } = toast;
  const config = typeConfig[type];
  const Icon = config.icon;

  // Enhanced message rendering with bold numbers
  const enhanceMessage = (msg: string): (string | JSX.Element)[] => {
    // Match patterns like "613 jobsets" or "1,382 jobs" and make them bold
    const parts = msg.split(/(\d+(?:,\d+)*\s+\w+)/g);
    return parts.map((part, index) => {
      if (/\d+(?:,\d+)*\s+\w+/.test(part)) {
        return <span key={index} className="font-bold">{part}</span>;
      }
      return part;
    });
  };

  return (
    <div
      className={`flex items-center gap-3 p-4 rounded-xl shadow-lg backdrop-blur-sm min-w-[320px] max-w-md ${config.bg} ${config.border} animate-in slide-in-from-top-2 fade-in duration-300`}
    >
      <div className="flex-shrink-0">
        <Icon className={`h-5 w-5 ${config.iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${config.text}`}>
          {enhanceMessage(message)}
        </p>
      </div>
      <button
        onClick={onClose}
        className={`flex-shrink-0 p-1.5 text-neutral-400 ${config.closeHover} rounded-lg transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 transform hover:scale-110`}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export default ToastItem;
